import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/contexts/DialogContext';
import { useToast } from '@/contexts/ToastContext';
import { billingService, Plan } from '@common/api/billingService';
import { addQueryParams } from '@common/utils/url';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import ContactSalesModal from './ContactSalesModal';
import PricingCard, { PlanData } from './PricingCard';

export interface UserPlanInfo {
    planCode?: string;
    billingCycle?: 'monthly' | 'yearly';
    status?: string;
    currentPeriodEnd?: string;
    trialEndAt?: string;
}

type PlanSelectorMode = 'onboarding' | 'upgrade';

interface PlanSelectorProps {
    // 模式：onboarding=引导流程（简化），upgrade=升级（完整流程）
    mode?: PlanSelectorMode;
    // 用户计划信息（用于显示当前计划状态）
    userPlan?: UserPlanInfo;
    // 禁用状态
    disabled?: boolean;
    // 自定义样式
    className?: string;
    // WorkspaceId（用于创建订阅时指定工作区）
    workspaceId: string;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
    mode = 'upgrade',
    userPlan,
    disabled = false,
    className,
    workspaceId
}) => {
    const {
        openSignInModal, user, profile, setProfile 
    } = useAuth();
    const { showToast } = useToast();
    const { showDialog } = useDialog();
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
    const [plans, setPlans] = useState<Plan[]>([]);
    const [isContactSalesOpen, setIsContactSalesOpen] = useState(false);
    const [loadingPlanName, setLoadingPlanName] = useState<string | undefined>();
    const [fetchingPlans, setFetchingPlans] = useState(true);

    useEffect(() => {
        const fetchPlans = async () => {
            setFetchingPlans(true);
            try {
                const response = await billingService.getPlans();
                console.log('获取到的 plans 数据:', response);
                // 处理可能的返回格式
                const plansData = response|| [];
                setPlans(plansData);
            } catch (err) {
                console.error('Failed to fetch plans', err);
            } finally {
                setFetchingPlans(false);
            }
        };
        fetchPlans();
    }, []);

    // 处理 billing cycle 切换
    const handleBillingCycleChange = (newCycle: 'monthly' | 'yearly') => {
        setBillingCycle(newCycle);
    };

    const getBasicPlanByCycle = (billingCycleParam: 'monthly' | 'yearly') => {
        return plans.find(p => p.code === 'BASIC' && p.billingCycle === billingCycleParam);
    };

    // 处理计划选择
    const handlePlanSelect = async (planName: string, billingCycleParam: 'monthly' | 'yearly') => {
        // Upgrade 模式：完整支付流程
        if (!user) {
            openSignInModal();
            return;
        }

        // 检查是否是切换 billing cycle（用户已经是 BASIC 计划）
        const isSwitchingBillingCycle = userPlan?.planCode === 'BASIC' && planName === 'Basic' && userPlan?.billingCycle !== billingCycleParam;

        // 如果是切换 billing cycle，显示确认弹窗
        if (isSwitchingBillingCycle) {
            const targetCycle = billingCycleParam === 'monthly' ? 'Monthly' : 'Yearly';
            const currentCycleLower = userPlan?.billingCycle === 'monthly' ? 'monthly' : 'yearly';

            const confirmed = await showDialog({
                title: `Switch to ${targetCycle} Billing?`,
                subtitle: `Switching to ${targetCycle} billing will take effect at the next renewal. You will continue to have access to your current plan until the end of your current ${currentCycleLower} cycle.`,
                confirmText: 'Confirm Switch',
                cancelText: 'Cancel',
            });

            if (!confirmed) {
                return;
            }
        }

        setLoadingPlanName(planName);

        try {
            // 直接使用 state 中的 plans，避免冗余的 API 调用
            const plan = getBasicPlanByCycle(billingCycleParam);

            if (!plan) {
                console.error('No plan found');
                showToast('Basic plan not found', 'error');
                return;
            }

            const successUrl = addQueryParams(window.location.origin + window.location.pathname, {
                pay_result: 'success',
                planCode: plan.code,
                planName: plan.name
            });
            const cancelUrl = addQueryParams(window.location.origin + window.location.pathname, { pay_result: 'cancel' });

            const subscriptionResponse = await billingService.createSubscription({
                planId: plan.id,
                successUrl,
                cancelUrl,
                axiosRequestConfig: {headers: {'X-Workspace-Id': workspaceId}}
            });

            if (subscriptionResponse.success) {
                if( subscriptionResponse.paymentUrl) {
                    window.location.href = subscriptionResponse.paymentUrl;
                }else{
                    showToast('Subscription created successfully', 'success');
                    // 更新 profile 中的 workspacePlan
                    if (profile && setProfile && profile.workspacePlan) {
                        setProfile({
                            ...profile,
                            workspacePlan: {
                                ...profile.workspacePlan,
                                status: 'active',
                                billingCycle: billingCycleParam,
                                planCode: plan.code,
                                planName: plan.name,
                                planId: plan.id,
                            }
                        });
                    }
                }
            }
        } catch (err) {
            showToast('Failed to create subscription', 'error');
            console.error(err);
        } finally {
            setLoadingPlanName(undefined);
        }
    };

    // 取消订阅后的续订
    const handleRenewSubscription = async (billingCycleParam: 'monthly' | 'yearly') => {
        if (!user) {
            openSignInModal();
            return;
        }

        setLoadingPlanName('Basic');

        try {
            const plan = getBasicPlanByCycle(billingCycleParam);

            if (!plan) {
                console.error('No plan found');
                showToast('Basic plan not found', 'error');
                return;
            }

            const successUrl = addQueryParams(window.location.origin + window.location.pathname, {
                pay_result: 'success',
                planCode: plan.code,
                planName: plan.name
            });
            const cancelUrl = addQueryParams(window.location.origin + window.location.pathname, { pay_result: 'cancel' });

            const renewResponse = await billingService.renewSubscription({
                planId: plan.id,
                successUrl,
                cancelUrl,
                axiosRequestConfig: {headers: {'X-Workspace-Id': workspaceId}}
            });

            if (renewResponse.success) {
                if (renewResponse.paymentUrl) {
                    window.location.href = renewResponse.paymentUrl;
                } else {
                    showToast('Subscription renewed successfully', 'success');
                    if (profile && setProfile && profile.workspacePlan) {
                        setProfile({
                            ...profile,
                            workspacePlan: {
                                ...profile.workspacePlan,
                                status: 'active',
                                billingCycle: billingCycleParam,
                                planCode: plan.code,
                                planName: plan.name,
                                planId: plan.id,
                            }
                        });
                    }
                }
            }
        } catch (err) {
            showToast('Failed to renew subscription', 'error');
            console.error(err);
        } finally {
            setLoadingPlanName(undefined);
        }
    };

    // 处理联系销售
    const handleContactSales = () => {
        setIsContactSalesOpen(true);
    };

    // 处理联系销售弹窗关闭
    const handleContactSalesClose = () => {
        setIsContactSalesOpen(false);
    };

    // 处理取消订阅（仅 upgrade 模式）
    const handleCancelSubscription = () => {
        billingService.createSubscriptionPortal(window.location.href)
            .then(response => {
                if (response.paymentType === 'shopify') {
                    window.location.reload();
                    return;
                }
                if (response.url) {
                    window.location.href = response.url;
                } else {
                    showToast('Failed to cancel subscription', 'error');
                }
            })
            .catch(error => {
                showToast('Failed to cancel subscription', 'error');
                console.log('error', error);
            });
    };

    const basicPlan = plans.find(p => p.code === 'BASIC' && p.billingCycle === billingCycle);
    const basicPrice = basicPlan
        ? (billingCycle === 'yearly' ? Math.round(basicPlan.priceCents / 12) : basicPlan.priceCents)
        : (billingCycle === 'monthly' ? 99 : 79);

    const userBillingCycle = userPlan?.billingCycle as 'monthly' | 'yearly' | undefined;
    const isExpired = Boolean(userPlan?.currentPeriodEnd && new Date().getTime() > new Date(userPlan.currentPeriodEnd).getTime());
    const isCanceled = userPlan?.status === 'pending_cancellation' && !isExpired;
    const isTrial = Boolean(userPlan?.trialEndAt && new Date(userPlan.trialEndAt).getTime() > new Date().getTime());

    const uiPlans: PlanData[] = [
        {
            name: 'Basic',
            price: basicPrice,
            description: 'For small businesses and startups',
            buttonText: 'Upgrade',
            isPopular: true,
            features: [
                'Full access to Bustly AI',
                'Connect chat channels',
                '7-day free trial included',
            ]
        },
        {
            name: 'Enterprise',
            price: 'Custom',
            description: 'For large teams & multi-brand portfolios',
            buttonText: 'Contact Sales',
            isPopular: false,
            isCustom: true,
            features: [
                'Everything in Basic, plus',
                '50+ integrations — All your tools connected',
                'Custom AI models — Trained on your business',
                'Dedicated success manager',
                'Enterprise SSO/SAML'
            ]
        }
    ];

    // 计算按钮文本和禁用状态
    const getButtonState = (plan: PlanData) => {
        let buttonText = plan.buttonText;
        let isDisabled = false;

        if (plan.isCustom || plan.name === 'Enterprise') {
            return { buttonText: 'Contact Sales', isDisabled: false };
        }

        // Onboarding 模式下的特殊处理
        if (mode === 'onboarding' && plan.name === 'Basic') {
            buttonText = 'Start Free Trial';
        } else {
            if (isExpired) {
                buttonText = 'Upgrade';
                isDisabled = false;
            } else if (isCanceled) {
                buttonText = 'Renew';
                isDisabled = false;
            } else if (userBillingCycle === billingCycle) {
                buttonText = 'Current Plan';
                isDisabled = true;
            } else if (userBillingCycle === 'monthly' && billingCycle === 'yearly') {
                buttonText = 'Switch to Yearly';
            } else if (userBillingCycle === 'yearly' && billingCycle === 'monthly') {
                buttonText = 'Switch to Monthly';
            }
        }

        return { buttonText, isDisabled };
    };

    return (
        <>
            <div className={clsx('w-full flex flex-col', className)}>
                {/* Billing Cycle Toggle */}
                <div className="flex justify-center mb-8">
                    <div className="bg-white border border-gray-200 shadow-sm p-1 rounded-2xl flex items-center relative inline-flex">
                        <button
                            type="button"
                            onClick={() => handleBillingCycleChange('monthly')}
                            className={clsx(
                                'w-32 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 relative z-10 flex items-center justify-center',
                                billingCycle === 'monthly'
                                    ? 'bg-text-main text-white shadow-md'
                                    : 'text-text-sub hover:text-text-main'
                            )}
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            onClick={() => handleBillingCycleChange('yearly')}
                            className={clsx(
                                'w-36 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 relative z-10 flex items-center justify-center gap-2',
                                billingCycle === 'yearly'
                                    ? 'bg-text-main text-white shadow-md'
                                    : 'text-text-sub hover:text-text-main'
                            )}
                        >
                            Yearly
                        </button>
                    </div>
                </div>

                {/* Plan Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch max-w-4xl mx-auto">
                    {uiPlans.map((plan, index) => {
                        const { buttonText, isDisabled: planIsDisabled } = getButtonState(plan);

                        return (
                            <PricingCard
                                key={index}
                                plan={{...plan, buttonText}}
                                loadingPlanName={loadingPlanName}
                                disabled={planIsDisabled || disabled}
                                fetching={fetchingPlans}
                                onAction={() => {
                                    if (plan.isCustom || plan.name === 'Enterprise') {
                                        handleContactSales();
                                    } else if (
                                        isCanceled
                                    ) {
                                        handleRenewSubscription(billingCycle);
                                    } else {
                                        handlePlanSelect(plan.name, billingCycle);
                                    }
                                }}
                            />
                        );
                    })}
                </div>

                {/* 取消订阅按钮（仅 upgrade 模式且有 BASIC 计划时显示） */}
                {mode === 'upgrade' && userPlan?.planCode === 'BASIC' ? (
                    <div
                        className="text-center mt-6 text-sm text-text-sub hover:text-text-main underline cursor-pointer transition-colors"
                        onClick={() => handleCancelSubscription()}
                    >
                        {window.isShopify ? 'Cancel subscription' : 'Edit billing'}
                    </div>
                ) : null}
            </div>

            {/* Contact Sales Modal */}
            <ContactSalesModal
                isOpen={isContactSalesOpen}
                onClose={handleContactSalesClose}
            />
        </>
    );
};

export default PlanSelector;
